import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { buildEmbedTheme } from "./embedTheme";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  selectScreen,
  setErrors,
  setIsRequest,
  setLoading,
  setCustomFields,
  setReduxSelectedParticipant,
  setReduxSignRequest,
  setUploadedFileNames,
} from "redux/appSlice";
import { setAuthToken } from "redux/authSlice";
import { setFeatures } from "redux/featuresSlice";
import strings from "global/constants/StringConstants";
import { FileField, RequestParticipant, SignRequest } from "models/interfaces";
import CustomStepper from "components/CustomStepper";
import FeatureKeys from "global/constants/FeatureKeys";
import { useFeatureState } from "hooks/use-feature";
import UploadDocuments from "screens/SignRequest/components/UploadDocuments";
import ApproversAndSigners from "screens/SignRequest/components/ApproversAndSigners";
import RequestConfiguration from "screens/SignRequest/components/RequestConfiguration";
import SignRequestEditor from "screens/SignRequest/components/SignRequestEditor";
import SmsConsentDialog from "screens/SignRequest/components/SmsConsentDialog";
import MobileApproversAndSigners from "screens/SignRequest/Mobile/steps/MobileApproversAndSigners";
import MobileSignRequestEditor from "screens/SignRequest/Mobile/steps/MobileSignRequestEditor";
import { recordSmsConsent } from "screens/SignRequest/SignRequestService";
import {
  fieldColor,
  setIdOnEachField,
  validateAndNormalizeSignRequestFieldTypes,
  validateField,
} from "screens/SignRequest/SignRequestHelper";
import { addFile, setCoordinates } from "screens/Shared/CommonHelper";
import {
  EmbedErrorCode,
  EmbedTheme,
  embedErrorCode,
  postEmbedError,
  postEmbedEventOnce,
  setEmbedContext,
  startEmbedChannel,
  subscribeTheme,
} from "./embedBridge";
import {
  EmbedEditorBundle,
  exchangeEmbedToken,
  fetchEmbedEditor,
  saveEmbedDraft,
  sendEmbedDraft,
} from "./embedService";
import { EmbedDone, EmbedError, EmbedLoading } from "./EmbedPanel";
import {
  embedDeliveryModeOf,
  embedEditorRequestId as getRequestId,
  setEmbedEmailBranding,
  setEmbedIdvMethods,
  withPositionalSignerIds,
} from "./EmbedEditorMode";
import { isTruthy, toPhoneParts } from "helpers/methods";

const MIN_WIDTH = 320;
const SEND_BAR_HEIGHT = 64;
const STEPPER_BAR_HEIGHT = 56;
/** The app's own four send-wizard steps, by the labels `validateField` switches on. */
const WIZARD_STEPS = [
  "Upload Document",
  "Approvers and Signers",
  "Sign Request Editor",
  "Email configurations",
] as const;

/** The step the SMS consent dialog guards: leaving it with any SMS recipient needs the sender's confirmation. */
const SIGNERS_STEP = 1;

/**
 * Errors that end the session: the partner mints a new token for them. Anything else (validation, plan
 * limit, credits, a server error) is shown inline and the wizard stays on its step, as the app's own
 * wizard shows a toast and stays.
 */
const TERMINAL_ERRORS: EmbedErrorCode[] = [
  "TOKEN_INVALID",
  "TOKEN_EXPIRED",
  "TOKEN_USED",
  "SESSION_EXPIRED",
  "ORIGIN_MISMATCH",
  "REQUEST_NOT_FOUND",
  "REQUEST_COMPLETED",
  "REQUEST_VOIDED",
  "REQUEST_EXPIRED",
  "REQUEST_DECLINED",
];

/** The draft's permission switches start from the server's defaults (all on), as in the app's wizard. */
const DEFAULT_SIGN_REQUEST_CONFIG = {
  allowDocumentDownload: true,
  sendNotificationOnCompletion: true,
  attachDocumentToMailOnCompletion: true,
  allowSignatureDelegation: true,
};

/** The one-time token arrives in the URL fragment, which browsers never send to a server or CDN log. */
const readFragmentToken = (): string => {
  const match = /(?:^#|&)token=([^&]+)/.exec(window.location.hash);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed escape (e.g. "%E0%A4%A") throws; treat it as no token so the panel shows TOKEN_INVALID.
    return "";
  }
};

/**
 * The draft as the wizard's editor step expects it -- the same shaping SignRequestComponent applies to a
 * draft opened from /edit/draft: signer colours, an approval block, unsignedDoc aliases and field ids.
 * Fields the partner pre-placed by API carry PDF-point coordinates only (no on-screen percentages), so
 * they are marked as existing fields, the way the editor treats fields read from a PDF.
 */
const prepareDraft = (draft: any): SignRequest => {
  const prepared = structuredClone({
    ...draft,
    approval: draft?.approval || {
      approvalNeeded: false,
      orderNeeded: false,
      reviewers: [],
    },
    signRequestConfig: {
      ...DEFAULT_SIGN_REQUEST_CONFIG,
      ...(draft?.signRequestConfig || {}),
    },
  }) as SignRequest;
  // A draft signer with no mode is one the partner posted to be emailed -- the server reads a blank as
  // EMAIL -- so it is filled in here; a signer left without a mode is then one this wizard added, which
  // signs in the partner's app (see embedDeliveryModeOf).
  prepared.signers = (prepared.signers || []).map(
    (signer: RequestParticipant) => ({
      ...signer,
      deliveryMode:
        signer.deliveryMode ||
        (strings.deliveryModes.email as RequestParticipant["deliveryMode"]),
      color: fieldColor[signer.id],
    }),
  );
  prepared.files = (Array.isArray(prepared.files) ? prepared.files : []).map(
    (file: any) => ({
      ...file,
      fields: Array.isArray(file.fields) ? file.fields : [],
      unsignedDoc: file.unsignedDoc || file.unsignedPdf || "",
      unsignedDocUrl: file.unsignedDocUrl || file.unsignedPdfUrl || "",
    }),
  );
  setIdOnEachField(prepared.files, { editDraft: true });
  prepared.files.forEach((file: any) =>
    file.fields.forEach((field: FileField) => {
      if (field.fieldPageX == null && field.existingField === undefined) {
        field.existingField = true;
      }
    }),
  );
  return prepared;
};

/**
 * /embed/editor -- the sender widget. Redeems the one-time token for an editor session, loads the draft
 * the partner created by API, and shows the app's four send-wizard steps (documents, signers, field
 * editor, email settings) with Back / Next and a final Send. Documents are saved to the draft as they
 * are added; signers, settings and fields are saved together just before the send, and the server
 * re-validates all of it. There is no resume: a reload has no token left, and the partner mints a new one.
 */
const EmbedEditorScreen = () => {
  const dispatch = useAppDispatch();
  const outerTheme = useTheme();
  const isMobile = useAppSelector(selectScreen);
  const { signRequest, errors, assignExistingFields } = useAppSelector(
    (state: RootState) => state.app,
  );
  const [phase, setPhase] = useState<"loading" | "ready" | "sending" | "sent">(
    "loading",
  );
  const [errorCode, setErrorCode] = useState<EmbedErrorCode | null>(null);
  // Set once the send has gone through: the session ends with it, so the expiry timer below must not
  // replace the "Sent" panel (and tell the partner "error") when the 60 minutes run out later.
  const sentRef = useRef(false);
  // Pending, Scheduled or InReview: what the "Sent" panel tells the sender.
  const sentStatusRef = useRef<string>(strings.pending);
  const [inlineError, setInlineError] = useState("");
  const [bundleTheme, setBundleTheme] = useState<EmbedTheme>({});
  const [parentTheme, setParentTheme] = useState<EmbedTheme>({});
  const [tooSmall, setTooSmall] = useState(window.innerWidth < MIN_WIDTH);
  const [currentStep, setCurrentStep] = useState(0);
  // The email-settings step's schedule picker. Delays between signers are set in Step 2, whose own
  // dialog holds that state, so the delayed-signer props here are only what the step's API asks for.
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [isScheduleReq, setIsScheduleReq] = useState(false);
  const [signerToDelay] = useState<Partial<RequestParticipant>>({ id: -1, order: -1 });
  const [, setIsDelaySigner] = useState(false);
  // The signers step on a phone: the app's mobile step keeps its Signers / Approvers tab here.
  const [signerTab, setSignerTab] = useState<"signer" | "approver">("signer");
  // SMS consent, as the app's wizard asks for it on leaving the signers step; the step to go to once given.
  const [smsConsentOpen, setSmsConsentOpen] = useState(false);
  const [smsConsentSubmitting, setSmsConsentSubmitting] = useState(false);
  const [smsConsentTarget, setSmsConsentTarget] = useState(SIGNERS_STEP + 1);

  const reminderState = useFeatureState(FeatureKeys.REMINDER_AUTOMATIC);
  const isReminderAvailable =
    reminderState.status === strings.FEATURE_STATUS_AVAILABLE;
  const expirationState = useFeatureState(FeatureKeys.ENVELOPE_AUTO_EXPIRATION);
  const isAutoExpirationAvailable =
    expirationState.status === strings.FEATURE_STATUS_AVAILABLE;

  const steps = useMemo(
    () =>
      WIZARD_STEPS.map((label, index) => ({
        label,
        isActive: currentStep === index,
        isCompleted: currentStep > index,
      })),
    [currentStep],
  );
  const isLastStep = currentStep === WIZARD_STEPS.length - 1;

  const fail = useCallback((code: EmbedErrorCode) => {
    setErrorCode(code);
    postEmbedError(code);
  }, []);

  useEffect(() => {
    const onResize = () => setTooSmall(window.innerWidth < MIN_WIDTH);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (tooSmall && !errorCode) postEmbedError("VIEWPORT_TOO_SMALL");
  }, [tooSmall, errorCode]);

  useEffect(() => subscribeTheme(setParentTheme), []);

  useEffect(() => {
    const token = readFragmentToken();
    if (window.location.hash) {
      // Drop the token from the address bar and history at once.
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
    if (!token) {
      // Nothing to exchange and no trusted parent origin: the panel is all the partner sees.
      setErrorCode("TOKEN_INVALID");
      return;
    }
    let expiryTimer: number | undefined;

    const start = async () => {
      let bundle: EmbedEditorBundle;
      try {
        const exchanged = await exchangeEmbedToken(token);
        if (exchanged.scope !== "creation" || !exchanged.sessionSecret) {
          setErrorCode("TOKEN_INVALID");
          return;
        }
        setEmbedContext({
          scope: "creation",
          requestId: exchanged.requestId,
          sessionSecret: exchanged.sessionSecret,
        });
        startEmbedChannel(exchanged.parentOrigin);
      } catch (error: any) {
        setErrorCode(embedErrorCode(error));
        return;
      }
      try {
        bundle = await fetchEmbedEditor(getRequestId());
      } catch (error: any) {
        fail(embedErrorCode(error));
        return;
      }

      // The editor components gate their own document calls on the sign permission. Grant it in this
      // memory-only store (no login, no token): the server authorises every call by the embed session.
      dispatch(
        setAuthToken({
          authenticated: false,
          resources: {
            [strings.resources.sign]: [
              strings.fetchPermission,
              strings.sendPermission,
              strings.addPermission,
              strings.updatePermission,
            ],
          },
        }),
      );
      dispatch(setFeatures(Array.isArray(bundle.features) ? bundle.features : []));
      dispatch(
        setCustomFields(Array.isArray(bundle.customFields) ? bundle.customFields : []),
      );
      dispatch(setIsRequest(true));

      const draft = prepareDraft(bundle.request);
      dispatch(setReduxSignRequest(draft));
      for (const file of draft.files) {
        dispatch(setUploadedFileNames([file.unsignedDoc]));
        addFile(file, true, dispatch, draft.id, false);
      }
      const firstSigner =
        draft.signers.find((signer: RequestParticipant) => !signer.cc) ||
        draft.signers[0];
      if (firstSigner) dispatch(setReduxSelectedParticipant(firstSigner));

      setEmbedEmailBranding(bundle.emailBranding);
      setEmbedIdvMethods(bundle.idvMethods);
      setBundleTheme(bundle.theme || {});
      if (bundle.expiresInSeconds > 0) {
        expiryTimer = window.setTimeout(
          () => {
            if (!sentRef.current) fail("SESSION_EXPIRED");
          },
          bundle.expiresInSeconds * 1000,
        );
      }
      setPhase("ready");
    };
    start();
    return () => window.clearTimeout(expiryTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Runs the wizard's own validation for one step; false (with the message shown) when it fails. */
  const validateStep = (index: number): boolean => {
    const { isValid, firstErrorMessage } = validateField(
      signRequest,
      errors,
      dispatch,
      { editDraft: true },
      WIZARD_STEPS[index],
      false,
      assignExistingFields,
      false,
    );
    if (!isValid) {
      setInlineError(firstErrorMessage || "Please complete this step.");
      postEmbedError("VALIDATION_FAILED");
    }
    return isValid;
  };

  const handleBack = () => {
    setInlineError("");
    dispatch(setErrors({}));
    setCurrentStep((step) => Math.max(0, step - 1));
  };

  /** Leaving the signers step with any SMS recipient needs the sender's consent confirmation first. */
  const needsSmsConsent = (from: number, to: number): boolean =>
    from === SIGNERS_STEP &&
    to > SIGNERS_STEP &&
    (signRequest?.signers ?? []).some((signer: RequestParticipant) =>
      (signer.deliveryChannels ?? []).includes(
        strings.deliveryChannels.sms as any,
      ),
    );

  const handleNext = () => {
    setInlineError("");
    if (!validateStep(currentStep)) return;
    const target = Math.min(WIZARD_STEPS.length - 1, currentStep + 1);
    if (needsSmsConsent(currentStep, target)) {
      setSmsConsentTarget(target);
      setSmsConsentOpen(true);
      return;
    }
    setCurrentStep(target);
  };

  /** Clicking a later step in the stepper validates every step before it, like the app's own wizard. */
  const handleChangeStep = (target: number) => {
    setInlineError("");
    if (target <= currentStep) {
      setCurrentStep(target);
      return;
    }
    for (let index = currentStep; index < target; index++) {
      if (!validateStep(index)) {
        setCurrentStep(index);
        return;
      }
    }
    if (needsSmsConsent(currentStep, target)) {
      setSmsConsentTarget(target);
      setSmsConsentOpen(true);
      return;
    }
    setCurrentStep(target);
  };

  /**
   * SMS consent confirmed: record the sender's attestation on the draft (the send refuses an SMS without
   * it) before moving on, exactly as the app's wizard does. Recording fails -> stay, with the reason.
   */
  const handleSmsConsentConfirm = async () => {
    const phoneNumbers: string[] = Array.from(
      new Set<string>(
        (signRequest?.signers ?? [])
          .filter(
            (signer: RequestParticipant) =>
              !signer.cc &&
              signer.deliveryMode !== strings.deliveryModes.inPerson &&
              // An embedded signer is never texted, whatever the row still holds.
              embedDeliveryModeOf(signer.deliveryMode) !==
                strings.deliveryModes.embedded &&
              (signer.deliveryChannels ?? []).includes(
                strings.deliveryChannels.sms as any,
              ),
          )
          .map((signer: RequestParticipant): string => {
            const parts = toPhoneParts(signer.phone);
            return parts ? `${parts.countryCode}${parts.number}` : "";
          })
          .filter((num: string) => Boolean(num)),
      ),
    );
    try {
      setSmsConsentSubmitting(true);
      await recordSmsConsent({
        consentReferenceId: getRequestId(),
        phoneNumbers,
        confirmed: true,
      });
      setSmsConsentOpen(false);
      setCurrentStep(smsConsentTarget);
    } catch (error: any) {
      // The endpoint's message is user-facing text (validation reasons, or "no consent agreement").
      setInlineError(
        typeof error?.message === "string" && error.message
          ? error.message
          : "There was an error on the server side. Please try again later.",
      );
    } finally {
      setSmsConsentSubmitting(false);
    }
  };

  const handleSend = async () => {
    setInlineError("");
    for (let index = 0; index < WIZARD_STEPS.length; index++) {
      if (!validateStep(index)) {
        setCurrentStep(index);
        return;
      }
    }
    // Same conversion the wizard runs before sendRequest: normalised field types and the on-screen
    // percentages turned into PDF-point coordinates.
    const normalized =
      validateAndNormalizeSignRequestFieldTypes(signRequest).normalizedRequest;
    const requestObject = setCoordinates(normalized);
    // Positional signer ids, and fields to match: the save and the send then name the same signers.
    const { signers, files } = withPositionalSignerIds(
      requestObject.signers || [],
      (requestObject.files || []).map((file: any) => ({
        unsignedPdf: file.unsignedPdf || file.unsignedDoc,
        fields: file.fields || [],
      })),
    );
    const needReminders = isReminderAvailable && !!requestObject.needReminders;
    const scheduleTime = isTruthy(requestObject.scheduleTime)
      ? requestObject.scheduleTime
      : null;
    const approvalNeeded = !!requestObject.approval?.approvalNeeded;
    // What the request is once sent, as the server sets it: a schedule wins, then approval.
    const sentStatus = scheduleTime
      ? strings.scheduled
      : approvalNeeded
        ? strings.inReview
        : strings.pending;
    try {
      setPhase("sending");
      dispatch(setLoading(true));
      // Step 1: save what the wizard edited (signers, approvers, email settings, permissions, schedule,
      // fields) on the draft; step 2: send it. The server re-validates everything and takes nothing but
      // these fields.
      await saveEmbedDraft(getRequestId(), {
        emailSubject: requestObject.emailSubject,
        emailMessage: requestObject.emailMessage,
        ...(isAutoExpirationAvailable && {
          expirationDays: Number(requestObject.expirationDays),
        }),
        needReminders,
        reminders: needReminders ? requestObject.reminders : null,
        signerOrderRequired: !!requestObject.signerOrderRequired,
        signers: signers.map((signer: any) => ({
          ...signer,
          status: "",
        })),
        files,
        explicitSettings: true,
        approval: approvalNeeded ? requestObject.approval : null,
        scheduleTime,
        signRequestConfig: requestObject.signRequestConfig,
      });
      await sendEmbedDraft(getRequestId(), files);
      sentRef.current = true;
      sentStatusRef.current = sentStatus;
      setPhase("sent");
      postEmbedEventOnce("send_completed", { status: sentStatus });
    } catch (error: any) {
      const code = embedErrorCode(error);
      if (TERMINAL_ERRORS.includes(code)) {
        fail(code);
      } else {
        // Recoverable: the server reopened the draft, so the sender can fix it and send again.
        setInlineError(
          String(error?.message || "")
            .replace(/^[A-Z_]+:\s*/, "")
            .trim() || "The request could not be sent. Please try again.",
        );
        postEmbedError(code);
        setPhase("ready");
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  const theme = useMemo(() => {
    const primaryColor = parentTheme.primaryColor || bundleTheme.primaryColor;
    const buttonTextColor =
      parentTheme.buttonTextColor || bundleTheme.buttonTextColor;
    // Partner's colours first, then the account branding in the bundle; none means the app's own theme.
    return buildEmbedTheme(outerTheme, primaryColor, buttonTextColor);
  }, [outerTheme, parentTheme, bundleTheme]);

  // A dead session or token is the more useful thing to tell the user than "enlarge the window".
  if (errorCode) return <EmbedError code={errorCode} />;
  if (tooSmall) {
    return (
      <EmbedError
        code="VIEWPORT_TOO_SMALL"
        onRetry={() => setTooSmall(window.innerWidth < MIN_WIDTH)}
      />
    );
  }
  if (phase === "loading") return <EmbedLoading />;
  if (phase === "sent") {
    return (
      <EmbedDone
        title={sentStatusRef.current === strings.scheduled ? "Scheduled" : "Sent"}
        message={
          sentStatusRef.current === strings.scheduled
            ? "The document will be sent at the scheduled time."
            : sentStatusRef.current === strings.inReview
              ? "The document is on its way to the approvers."
              : "The document is on its way to the signers."
        }
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <SmsConsentDialog
        open={smsConsentOpen}
        confirming={smsConsentSubmitting}
        onCancel={() => setSmsConsentOpen(false)}
        onConfirm={handleSmsConsentConfirm}
      />
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
        <Box
          sx={{
            height: STEPPER_BAR_HEIGHT,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            px: 2,
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <CustomStepper
            steps={steps}
            currentStep={currentStep}
            handleChangeStep={handleChangeStep}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {currentStep === 0 && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <UploadDocuments bulk />
            </Box>
          )}
          {currentStep === 1 && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              {/* The app's own mobile step on a phone: the desktop rows hide delivery and security there. */}
              {isMobile ? (
                <MobileApproversAndSigners
                  tabValue={signerTab}
                  setTabValue={setSignerTab}
                />
              ) : (
                <ApproversAndSigners />
              )}
            </Box>
          )}
          {currentStep === 2 &&
            (isMobile ? (
              <MobileSignRequestEditor />
            ) : (
              <SignRequestEditor
                setLoader={(value: boolean) => dispatch(setLoading(value))}
              />
            ))}
          {currentStep === 3 && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <RequestConfiguration
                openDatePicker={openDatePicker}
                setOpenDatePicker={setOpenDatePicker}
                setIsScheduleRequest={setIsScheduleReq}
                isScheduleReq={isScheduleReq}
                signerToDelay={signerToDelay}
                setIsDelaySigner={setIsDelaySigner}
                setLoader={(value: boolean) => dispatch(setLoading(value))}
                editDraft
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            height: SEND_BAR_HEIGHT,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            px: 2,
            borderTop: 1,
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          {inlineError && (
            <Alert
              severity="error"
              role="alert"
              sx={{ flex: 1, py: 0 }}
              onClose={() => setInlineError("")}
            >
              {inlineError}
            </Alert>
          )}
          {currentStep > 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={phase === "sending"}
            >
              Back
            </Button>
          )}
          {isLastStep ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSend}
              disabled={phase === "sending"}
              startIcon={
                phase === "sending" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              Send
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={phase === "sending"}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default EmbedEditorScreen;
